import { useState } from "react";
import Button from "@components/ui/Button";
import ErrorBanner from "@components/shared/ErrorBanner";
import Table, {
  TableBody, TableCell, TableHead, TableHeaderCell, TableRow,
} from "@components/ui/Table";

const inputClass =
  "w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500";

/**
 * The procedures a campaign can offer, with how long each one books for.
 *
 * A procedure's code is fixed once created: campaigns and outreach payloads refer
 * to it, and the API rejects the key outright. Retiring one keeps it on past
 * campaigns instead of deleting it. Edits here save immediately, unlike the
 * settings above, because each one is its own row on the server.
 */
export default function ProceduresEditor({
  treatments, error, onDismissError, onAdd, onEdit,
}) {
  const [problem, setProblem] = useState(null);
  const [draft, setDraft] = useState({ code: "", name: "", minutes: "30" });
  // What is being typed right now, per field. Everything else shows what the
  // server holds: React ignores defaultValue on a mounted input, so an
  // uncontrolled box kept displaying a rename the server had refused, and a
  // name it had trimmed or truncated on the way in.
  const [pending, setPending] = useState({});

  const cell = (treatmentId, field) => `${treatmentId}:${field}`;

  const shown = (treatmentId, field, stored) => {
    const key = cell(treatmentId, field);
    return key in pending ? pending[key] : stored;
  };

  const type = (treatmentId, field, value) =>
    setPending((current) => ({ ...current, [cell(treatmentId, field)]: value }));

  const settle = (treatmentId, field) => setPending((current) => {
    const key = cell(treatmentId, field);
    if (!(key in current)) return current;
    const next = { ...current };
    delete next[key];
    return next;
  });

  const commitName = async (treatment, value) => {
    const name = value.trim();
    if (!name) {
      // Left as typed, so the clinic can see what it has to fix.
      setProblem("A procedure needs a name.");
      return;
    }
    if (name === treatment.display_name) {
      settle(treatment.id, "name");
      return;
    }
    setProblem(null);
    await onEdit(treatment.id, { displayName: name });
    // Stored, normalised or refused, the box goes back to showing what the
    // refetch says is on the server.
    settle(treatment.id, "name");
  };

  const commitMinutes = async (treatment, value) => {
    const minutes = Number(value);
    if (!Number.isInteger(minutes) || minutes < 1) {
      setProblem("An appointment length must be a whole number of minutes.");
      return;
    }
    if (minutes === treatment.default_duration_minutes) {
      settle(treatment.id, "minutes");
      return;
    }
    setProblem(null);
    await onEdit(treatment.id, { durationMinutes: minutes });
    settle(treatment.id, "minutes");
  };

  const add = async () => {
    const code = draft.code.trim().toUpperCase();
    const name = draft.name.trim();
    const minutes = Number(draft.minutes);
    if (!code || !name) {
      setProblem("A new procedure needs a code and a name.");
      return;
    }
    if (!Number.isInteger(minutes) || minutes < 1) {
      setProblem("An appointment length must be a whole number of minutes.");
      return;
    }
    setProblem(null);
    const ok = await onAdd({ code, displayName: name, durationMinutes: minutes });
    if (ok) setDraft({ code: "", name: "", minutes: "30" });
  };

  return (
    <div className="space-y-3">
      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell>Code</TableHeaderCell>
            <TableHeaderCell>Name</TableHeaderCell>
            <TableHeaderCell className="w-32">Minutes</TableHeaderCell>
            <TableHeaderCell className="w-40">
              <span className="sr-only">Actions</span>
            </TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(treatments || []).map((treatment) => (
            <TableRow key={treatment.id} className={treatment.is_active ? "" : "bg-gray-50"}>
              <TableCell className="text-sm text-gray-500">
                {treatment.code}
                {!treatment.is_active && (
                  <span className="block text-xs text-gray-400">Not offered</span>
                )}
              </TableCell>
              <TableCell>
                <input className={inputClass}
                       value={shown(treatment.id, "name", treatment.display_name)}
                       aria-label={`Name for ${treatment.code}`}
                       onChange={(e) => type(treatment.id, "name", e.target.value)}
                       onBlur={(e) => commitName(treatment, e.target.value)} />
              </TableCell>
              <TableCell>
                <input className={inputClass} type="number" min="1"
                       value={shown(treatment.id, "minutes",
                                    treatment.default_duration_minutes)}
                       aria-label={`Minutes for ${treatment.code}`}
                       onChange={(e) => type(treatment.id, "minutes", e.target.value)}
                       onBlur={(e) => commitMinutes(treatment, e.target.value)} />
              </TableCell>
              <TableCell>
                <Button
                  color="light"
                  size="xs"
                  aria-label={treatment.is_active
                    ? `Stop offering ${treatment.display_name}`
                    : `Offer ${treatment.display_name} again`}
                  onClick={() => onEdit(treatment.id, { isActive: !treatment.is_active })}
                >
                  {treatment.is_active ? "Stop offering" : "Offer again"}
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {/* An empty table after a failed load says nothing about the clinic;
              the banner above already explains it, and telling a clinic that
              has procedures it has none would be worse than saying nothing. */}
          {(treatments || []).length === 0 && !error && (
            <TableRow>
              <TableCell colSpan={4} className="text-center text-sm text-gray-500">
                No procedures yet. Add the ones a campaign can offer.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* One place for everything that went wrong in this section, rather than
          at the top of a five-section page: directly under the rows whose
          actions can fail, and directly above the form that can 409. */}
      <ErrorBanner message={error} onDismiss={onDismissError} />
      {problem && <p className="text-xs text-red-600">{problem}</p>}

      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="new-code">
            New code
          </label>
          <input id="new-code" className={inputClass} value={draft.code} placeholder="CLEAN"
                 onChange={(e) => setDraft({ ...draft, code: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="new-name">
            New name
          </label>
          <input id="new-name" className={inputClass} value={draft.name} placeholder="Cleaning"
                 onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="new-minutes">
            New minutes
          </label>
          <input id="new-minutes" className={inputClass} type="number" min="1"
                 value={draft.minutes}
                 onChange={(e) => setDraft({ ...draft, minutes: e.target.value })} />
        </div>
        <Button color="primary" size="sm" onClick={add}>Add procedure</Button>
      </div>
    </div>
  );
}
